import React, { useState } from "react";
import { Hexagon, CheckCircle2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function PublicForm() {
  const [form, setForm] = useState({
    company_name: "", website_url: "", client_poc_name: "",
    client_poc_email: "", client_poc_number: "", category: "",
    opportunity_type: [], remarks: "",
  });
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const url = "/public/leads?" + params.toString();
      await api.post(url, form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-4 peach-bg">
      <div className="bg-white max-w-lg w-full rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-orange-500 text-white grid place-items-center"><Hexagon size={18} /></div>
          <div>
            <div className="text-base font-bold text-slate-900">LeadHub</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider">Talk to sales</div>
          </div>
        </div>

        {done ? (
          <div className="text-center py-10">
            <CheckCircle2 size={42} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-semibold text-slate-900">Thanks! We’ll be in touch.</h2>
            <p className="text-sm text-slate-500 mt-2">Our team will reach out within 1 business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="public-lead-form" className="space-y-3">
            <h2 className="text-xl font-semibold text-slate-900 mb-1">Tell us about your business</h2>
            <p className="text-sm text-slate-500 mb-3">Share a few details and we’ll route you to the right specialist.</p>
            <Input label="Company name *" value={form.company_name} onChange={(v) => update("company_name", v)} required testid="pf-company" />
            <Input label="Website URL" value={form.website_url} onChange={(v) => update("website_url", v)} placeholder="https://" testid="pf-website" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Your name" value={form.client_poc_name} onChange={(v) => update("client_poc_name", v)} testid="pf-name" />
              <Input label="Email" type="email" value={form.client_poc_email} onChange={(v) => update("client_poc_email", v)} testid="pf-email" />
            </div>
            <Input label="Phone" value={form.client_poc_number} onChange={(v) => update("client_poc_number", v)} testid="pf-phone" />
            <Input label="Category" value={form.category} onChange={(v) => update("category", v)} testid="pf-category" placeholder="e.g. Fashion & Apparel" />
            <Input label="Tell us more" textarea value={form.remarks} onChange={(v) => update("remarks", v)} testid="pf-remarks" />
            <button
              data-testid="pf-submit"
              disabled={saving}
              className="mt-2 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-md inline-flex items-center justify-center gap-1.5 shadow-sm"
            >
              {saving ? "Sending…" : "Send enquiry"} {!saving && <ArrowRight size={14} />}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, textarea, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-orange-500 outline-none" />
      ) : (
        <input data-testid={testid} type={type} required={required} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-orange-500 outline-none" />
      )}
    </div>
  );
}
